/**
 * PressingPage — DTF Heat Press station (Printer-page parity).
 *
 * DELIBERATE SIBLING of the DTG print station (PrintStationPage): same card
 * anatomy (order header + badges → preview tiles beside the identity zone) and
 * the same sticky bottom action bar — "Press front" / "Press back"
 * for duals (buttons flip green when a side is done), a single "Press" for
 * front-only, then a green scan-next affordance once fully pressed. Keep the
 * two stations visually in lockstep: an operator moving between them should
 * not have to relearn the layout.
 *
 * The film↔garment match happens at the MATCHING station (slot released
 * there) — the press only resolves the already-MATCHED pairing and confirms
 * per side. LABEL_PRINTED is PICKED-equivalent (Brett 2026-07-22): the press
 * accepts pre-pick garments; the server gates the rest.
 *
 * Follows PRODUCTION_DESIGN.md: scanner-first, ≥48px targets, modal-level
 * errors with audio, semantic colour for state, one blocking modal at a time.
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from '@/contexts/LocationContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { QRScannerInput } from '@/features/catcher-station';
import { useScanFeedback } from '@/hooks/useScanFeedback';
import { useScannerAutoFocus } from '@/hooks/useScannerAutoFocus';
import { ScanErrorModal } from '@/components/ui/scan-error-modal';
import { FilmRedoDialog } from '@/components/production/FilmRedoDialog';
import { FilmSidesBadge } from '@/components/production/FilmSidesBadge';
import { ProductionImageLightbox } from '@/components/production/ProductionImageLightbox';
import {
  EMPTY_PRODUCTION_LIGHTBOX_DATA,
  ProductionPreviewStrip,
  buildFinishedGarmentPreviewEntries,
  buildProductionLightboxData,
  productionPreviewKey,
  type ProductionPreviewKind,
} from '@/components/production/ProductionPreviewTile';
import { useProductionLightbox } from '@/components/production/useProductionLightbox';
import { cleanGarmentDisplayName } from '@/lib/garment-display-name';
import { usePressMutations } from '../hooks/usePressMutations';
import { trpc } from '@/lib/trpc';
import { extractGarmentId } from '@/lib/qr-utils';
import { getUserMessage, parseOrderOnHold, parseFilmSideMismatch } from '@/lib/trpc-error-utils';
import { playSuccessBeep } from '@/lib/sounds';
import { useBackendHeartbeat } from '@/hooks/useBackendHeartbeat';
import { ConnectionStatusBanner } from '@/components/ui/connection-status-banner';
import { toast } from 'sonner';
import {
  Flame, ArrowLeft, CheckCircle, Loader2, AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';

type GarmentForPress = Awaited<ReturnType<ReturnType<typeof trpc.useUtils>['pressing']['getGarmentForPress']['fetch']>>;
type PressSide = 'front' | 'back';

export function PressingPage() {
  const { t } = useTranslation('production');
  const { selectedLocation } = useLocation();
  const heartbeat = useBackendHeartbeat();
  const feedback = useScanFeedback();
  const press = usePressMutations();
  const utils = trpc.useUtils();
  const redoMutation = trpc.catcher.createRedo.useMutation();

  const [garment, setGarment] = useState<GarmentForPress | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  // Per-side press progress (seeded from the lookup; updated on confirm).
  const [sidesDone, setSidesDone] = useState<{ front: boolean; back: boolean }>({ front: false, back: false });
  const [pressingSide, setPressingSide] = useState<PressSide | null>(null);
  const [pressed, setPressed] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  // Clear on location change.
  useEffect(() => {
    setGarment(null);
    setSidesDone({ front: false, back: false });
    setPressed(false);
  }, [selectedLocation?.id]);

  const clearGarment = useCallback(() => {
    setGarment(null);
    setSidesDone({ front: false, back: false });
    setPressed(false);
  }, []);

  // Garment image lightbox. Declared with the other focus-trapping overlays and
  // ABOVE the scanner pause below that reads it (PRODUCTION_DESIGN §6.2).
  //
  // ONE FLAT LIST for the garment on screen, from the SAME pure builder the
  // card renders from, so a click opens that exact image. The overlay's state
  // (the clicked tile's fallthrough, the empty-list guard, the reset on scan
  // away) is `useProductionLightbox`, shared with the other stations.
  const lightboxData = useMemo(
    () =>
      garment
        ? buildProductionLightboxData([
            {
              subjectId: garment.garmentUnitId,
              subject: cleanGarmentDisplayName(garment.productName, garment.color, garment.size),
              entries: buildFinishedGarmentPreviewEntries(garment),
            },
          ])
        : EMPTY_PRODUCTION_LIGHTBOX_DATA,
    [garment],
  );
  const lightbox = useProductionLightbox({
    entries: lightboxData.entries,
    indexByKey: lightboxData.indexByKey,
    resetKey: garment?.garmentUnitId,
  });
  const openLightboxAt = lightbox.openAt;

  const handleOpenPreview = useCallback(
    (kind: ProductionPreviewKind, sourceIndex: number) => {
      if (!garment) return;
      openLightboxAt(productionPreviewKey(garment.garmentUnitId, kind), sourceIndex);
    },
    [openLightboxAt, garment],
  );

  // `anyModalOpen` also disables the scanner INPUT, which the lightbox must not
  // do: a disabled input cannot hold focus, so the shared lightbox's refocus on
  // close would land on nothing. The lightbox pauses the global refocus
  // listener only, and leaves the input mounted and focusable behind it.
  const anyModalOpen = feedback.isModalOpen || reportOpen;
  useScannerAutoFocus({ paused: anyModalOpen || lightbox.open });

  // Imperative garment lookup (the press query keys off garmentUnitId).
  const lookupGarment = useCallback(async (garmentUnitId: string) => {
    if (!selectedLocation?.id) return;
    setIsLookingUp(true);
    try {
      const result = await utils.pressing.getGarmentForPress.fetch({
        garmentUnitId,
        locationId: selectedLocation.id,
      });
      setGarment(result);
      setSidesDone({
        front: !!result.sides.frontPressedAt,
        back: !!result.sides.backPressedAt,
      });
      setPressed(result.garmentStatus === 'PRESSED');
      } catch (error) {
      setGarment(null);
      const onHold = parseOrderOnHold(error);
      if (onHold.isOnHold) {
        // Order paused (reversible) — amber, set aside. DTF press is the busiest
        // DTF QC scan point, so the on-hold block matters here as at catcher.
        feedback.triggerOrderOnHold(onHold.holdReason);
      } else {
        // Every scan is a deliberate action: show the error every time. A
        // repeat used to be swallowed, so a rescan beeped with nothing on screen.
        feedback.triggerError(t('pressing.lookupError'), getUserMessage(error));
      }
    } finally {
      setIsLookingUp(false);
    }
    // feedback intentionally excluded — recreated every render (unstable-object gotcha)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id, utils.pressing.getGarmentForPress, t]);

  const handlePressSide = useCallback(async (side: PressSide) => {
    if (!garment || !garment.film || !selectedLocation?.id) return;
    if (pressingSide) return;
    setPressingSide(side);
    try {
      const res = await press.confirmPress({
        garmentUnitId: garment.garmentUnitId,
        filmUnitId: garment.film.id,
        locationId: selectedLocation.id,
        side,
      });
      if (res.success) {
        setSidesDone((s) => ({ ...s, [side]: true }));
        if (res.fullyPressed) {
          setPressed(true);
          playSuccessBeep();
          void utils.pressing.getGarmentForPress.invalidate();
          toast.success(t('pressing.pressed'), { description: garment.productName, duration: 3000 });
        } else {
          playSuccessBeep();
        }
      }
    } catch (error) {
      // Phase 50.1: the film does not carry the side we just submitted. This is
      // the stale-tab window (Vercel deploys after Railway, so a tab loaded
      // before the back-only fix can still submit 'front' for a back-only
      // film) and the pre-flight-read↔RPC race. Without this branch the floor
      // saw raw Postgres RAISE text; with it they get the one instruction that
      // matters — press the OTHER side — and the card is re-fetched so the
      // buttons stop offering the impossible one.
      const mismatch = parseFilmSideMismatch(error);
      if (mismatch.isSideMismatch) {
        feedback.triggerError(
          t('pressing.sideMismatchTitle'),
          mismatch.requiredSide === 'back'
            ? t('pressing.sideMismatchBack')
            : mismatch.requiredSide === 'front'
              ? t('pressing.sideMismatchFront')
              : t('pressing.sideMismatchUnknown'),
        );
        void lookupGarment(garment.garmentUnitId);
      } else {
        feedback.triggerError(t('pressing.pressError'), getUserMessage(error));
      }
    } finally {
      setPressingSide(null);
    }
    // feedback excluded (unstable). press/utils/t/lookupGarment stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [garment, selectedLocation?.id, press, pressingSide, utils.pressing.getGarmentForPress, lookupGarment, t]);

  // Scan: no garment → look it up; same garment → no-op; different valid
  // garment → fresh lookup (the film label carries the garment's QR, so
  // scanning either the garment or its film opens the same unit).
  const handleScan = useCallback((qrCode: string) => {
    const scannedId = extractGarmentId(qrCode);
    if (!scannedId) {
      feedback.triggerError(t('pressing.scanError'), t('pressing.scanErrorDesc'));
      return;
    }
    if (garment && scannedId === garment.garmentUnitId) {
      if (pressed) toast.info(t('pressing.pressed'), { duration: 1500 });
      return;
    }
    if (garment) clearGarment();
    void lookupGarment(scannedId);
    // feedback excluded (unstable).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [garment, pressed, lookupGarment, clearGarment, t]);

  const handleReportFilmNotFound = useCallback(async () => {
    if (!garment || !selectedLocation?.id) return;
    try {
      await press.reportFilmNotFound({ garmentUnitId: garment.garmentUnitId, locationId: selectedLocation.id });
      toast.success(t('pressing.filmNotFoundConfirmed'), { duration: 3000 });
      clearGarment();
    } catch (error) {
      feedback.triggerError(t('pressing.reportFailed'), getUserMessage(error));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [garment, selectedLocation?.id, press, clearGarment, t]);

  const handleReportConfirm = useCallback(async (reason: string) => {
    if (!garment || !selectedLocation?.id) return;
    try {
      const result = await redoMutation.mutateAsync({
        garmentUnitId: garment.garmentUnitId,
        locationId: selectedLocation.id,
        reason,
        redoSubtype: 'film_only' as const,
      });
      if (result.success) {
        setReportOpen(false);
        playSuccessBeep();
        toast.success(t('pressing.reportDone'), { duration: 3000 });
        clearGarment();
      }
    } catch (error) {
      setReportOpen(false);
      feedback.triggerError(t('pressing.reportFailed'), getUserMessage(error));
    }
    // feedback excluded (unstable).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [garment, selectedLocation?.id, redoMutation, clearGarment, t]);

  // Location guard.
  if (!selectedLocation) {
    return (
      <div className="container mx-auto px-6 py-6 max-w-7xl animate-enter">
        <Card>
          <CardContent className="py-16 text-center">
            <Flame className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg text-muted-foreground">{t('pressing.selectLocation')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasGarment = !!garment;
  const film = garment?.film ?? null;
  // MATCHED is the ready state; STORED still presses (films racked before the
  // Matching station existed).
  const filmReady = !!film && (film.status === 'MATCHED' || film.status === 'STORED');
  const isDual = film?.isDual ?? false;
  const isBackOnly = film?.isBackOnly ?? false;
  /** The one side a non-dual film carries — what the single Press button
   *  submits and what its label/spinner keys off. Derived here (not inside the
   *  JSX branch) so the button stays a plain expression. */
  const singleSide: PressSide = isBackOnly ? 'back' : 'front';

  return (
    <div className="container mx-auto px-6 py-6 max-w-7xl space-y-6 animate-enter flex flex-col min-h-[calc(100dvh-4.5rem)]">
      {/* ARIA live region (i18n). */}
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {isLookingUp && t('lookingUp')}
        {garment && t('pressing.liveStatus', { product: garment.productName })}
      </div>

      {/* Header */}
      <div className="flex items-center gap-3">
        {hasGarment && (
          <Button variant="ghost" onClick={clearGarment} className="shrink-0 gap-1.5" aria-label={t('backTo', { station: t('pressing.title') })}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <div className="p-2 rounded-lg bg-orange-500/10">
          <Flame className="h-6 w-6 text-orange-600 dark:text-orange-400" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">{t('pressing.title')}</h1>
          <p className="text-muted-foreground">{t('pressing.description')}</p>
        </div>
      </div>

      <ConnectionStatusBanner heartbeat={heartbeat} />

      <div className="flex-1 space-y-6">
        {/* ACTIVE: garment card (Printer-page anatomy) */}
        {hasGarment && garment && (
          <>
            <PressGarmentCard
              garment={garment}
              pressed={pressed}
              filmReady={filmReady}
              onOpenPreview={handleOpenPreview}
            />
            {/* Hidden scanner — scan a new garment to switch units. */}
            <div className="sr-only">
              <QRScannerInput onScan={handleScan} disabled={anyModalOpen} />
            </div>
          </>
        )}

        {/* LOADING */}
        {!hasGarment && isLookingUp && (
          <Card>
            <CardContent className="py-16 text-center">
              <Loader2 className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-spin" />
              <p className="text-lg text-muted-foreground">{t('loadingItem')}</p>
            </CardContent>
          </Card>
        )}

        {/* IDLE: scanner card */}
        {!hasGarment && !isLookingUp && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">{t('pressing.scanGarment')}</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <QRScannerInput onScan={handleScan} disabled={anyModalOpen} isLoading={isLookingUp} />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Sticky action bar — mirrors PrintStationPage's bottom bar. */}
      {hasGarment && garment && (
        <div className="sticky bottom-0 z-10 bg-background pb-4 pt-4 border-t space-y-3">
          {pressed ? (
            <div className="space-y-2">
              <div className="flex items-center justify-center gap-2 p-2 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                <p className="text-sm font-semibold text-green-700 dark:text-green-300">
                  {t('pressing.pressedScanNext')}
                </p>
              </div>
              <Button size="lg" variant="outline" className="w-full h-12 text-base" onClick={clearGarment}>
                {t('pressing.scanGarment')}
              </Button>
            </div>
          ) : !film ? (
            <Button
              size="lg"
              className="w-full h-16 text-xl font-semibold"
              variant="destructive"
              onClick={handleReportFilmNotFound}
              disabled={press.isReporting}
            >
              {t('pressing.reportFilmNotFound')}
            </Button>
          ) : !filmReady ? (
            <div
              className="w-full h-16 flex items-center justify-center gap-2 rounded-md border-2 border-dashed border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 text-lg font-semibold"
              role="status"
              aria-live="polite"
            >
              <AlertCircle className="h-5 w-5" />
              {t('pressing.filmNotReady')}
            </div>
          ) : isDual ? (
            /* Dual — Press front / Press back (Printer dual-buttons twin). */
            <div className="flex gap-4">
              <Button
                size="lg"
                className={cn(
                  'flex-1 h-16 text-xl font-semibold',
                  sidesDone.front && 'bg-green-600 hover:bg-green-700 border-green-600 text-white',
                )}
                variant={sidesDone.front ? 'outline' : 'default'}
                onClick={() => void handlePressSide('front')}
                disabled={pressingSide !== null || sidesDone.front}
              >
                {sidesDone.front ? (
                  <><CheckCircle className="h-5 w-5 mr-2" />{t('pressing.frontPressed')}</>
                ) : pressingSide === 'front' ? t('pressing.pressing') : t('pressing.pressFront')}
              </Button>
              <Button
                size="lg"
                className={cn(
                  'flex-1 h-16 text-xl font-semibold',
                  sidesDone.back && 'bg-green-600 hover:bg-green-700 border-green-600 text-white',
                )}
                variant={sidesDone.back ? 'outline' : 'default'}
                onClick={() => void handlePressSide('back')}
                disabled={pressingSide !== null || sidesDone.back}
              >
                {sidesDone.back ? (
                  <><CheckCircle className="h-5 w-5 mr-2" />{t('pressing.backPressed')}</>
                ) : pressingSide === 'back' ? t('pressing.pressing') : t('pressing.pressBack')}
              </Button>
            </div>
          ) : (
            /* Single design — one full-width Press button (Printer single twin).
             * Phase 50.1 plan 05: a back-only film has no front design, so it
             * must submit `singleSide` ('back') and stamp back_pressed_at —
             * submitting 'front' here (the old hard-coded call) is what
             * recorded the wrong column before this fix. `singleSide` is
             * derived beside `isBackOnly` above; this used to be an inline IIFE
             * purely to hold that one `const`. */
            <Button
              size="lg"
              className="w-full h-16 text-xl font-semibold"
              onClick={() => void handlePressSide(singleSide)}
              disabled={pressingSide !== null}
            >
              {pressingSide === singleSide
                ? t('pressing.pressing')
                : isBackOnly
                  ? t('pressing.pressBack')
                  : t('pressing.press')}
            </Button>
          )}

          {/* Report issue — canonical film-only redo (secondary). */}
          {!pressed && film && (
            <Button
              variant="outline"
              className="w-full h-12 text-base"
              onClick={() => setReportOpen(true)}
              disabled={redoMutation.isPending || pressingSide !== null}
            >
              {t('pressing.reportIssue')}
            </Button>
          )}
        </div>
      )}

      {/* Garment image lightbox — arrows flick between this garment's sides;
          closing hands the scanner back (§6.2). */}
      <ProductionImageLightbox
        open={lightbox.open}
        onOpenChange={lightbox.setOpen}
        images={lightbox.images}
        index={lightbox.index}
        onIndexChange={lightbox.setIndex}
        copy={{
          title: t('pressing.preview.lightboxTitle'),
          description: t('pressing.preview.lightboxDescription'),
          previousLabel: t('preview.previousImage'),
          nextLabel: t('preview.nextImage'),
        }}
      />

      <FilmRedoDialog
        open={reportOpen}
        onOpenChange={(o) => { if (!o) setReportOpen(false); }}
        onConfirm={handleReportConfirm}
        isPending={redoMutation.isPending}
      />
      <ScanErrorModal {...feedback.modalProps} />
    </div>
  );
}

// ============================================================================
// PressGarmentCard — Printer-page card anatomy: order header + badges →
// finished-garment preview tiles → identity zone. Mirrors PrintGarmentCard;
// keep the two in visual lockstep.
//
// THE PREVIEW is a REFERENCE, never the instruction. The operator has a
// matched film in their hand and the tiles let them catch a film that does not
// belong on this garment. Which face to press is still decided by the film's
// own FilmSidesBadge and by the press buttons: a garment can print both sides
// while the film in hand carries one.
//
// The source chain, the tier-aware captions, the skeleton, the fallback walk
// and the fixed footprint all live in the shared production primitive
// (`@/components/production/ProductionPreviewTile`). What was here before was
// a hand-rolled ladder that ended on `imageUrl`, the blank-garment photo, with
// nothing telling the operator it was a stand-in.
// ============================================================================

function PressGarmentCard({
  garment,
  pressed,
  filmReady,
  onOpenPreview,
}: {
  garment: NonNullable<GarmentForPress>;
  pressed: boolean;
  filmReady: boolean;
  /** Opens the page lightbox at the source this tile ACTUALLY rendered. */
  onOpenPreview?: (kind: ProductionPreviewKind, sourceIndex: number) => void;
}) {
  const { t } = useTranslation('production');
  const film = garment.film;
  // Size and colour are their own badges below, so the variant suffix Shopify
  // bakes onto the title ("Heavyweight Tee - Black / L") would print the same
  // two facts twice and push the identifying part off the line. Catching and
  // Print already strip it; the press has to agree or one garment reads under
  // two different names on two screens.
  const displayName = cleanGarmentDisplayName(garment.productName, garment.color, garment.size);

  // The garment-preview policy, shared with Print, Catching and Packing: a
  // tile per side that has a mockup on file, or one combined preview for a
  // custom. Pure, so PressingPage builds its lightbox from the very same call.
  const previewEntries = buildFinishedGarmentPreviewEntries(garment);

  return (
    <Card className={cn(
      'transition-all',
      pressed && 'border-l-4 border-l-emerald-500',
      garment.isRedo && !pressed && 'border-l-4 border-l-amber-500',
    )}>
      <CardContent className="pt-6">
        {/* Order header — Printer twin. */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <span className="text-lg font-semibold">{garment.orderNumber || displayName}</span>
          </div>
          <div className="flex items-center gap-2">
            {garment.isRedo && <Badge variant="destructive">REDO</Badge>}
            <Badge className="bg-fuchsia-100 text-fuchsia-800 hover:bg-fuchsia-100 dark:bg-fuchsia-900/50 dark:text-fuchsia-200">DTF</Badge>
            {/* Which side(s) this film carries — the fact that decides which
                face of the garment the operator presses next, so it is
                station-scale (§4.2) and colour+icon+text differentiated.
                Shared with the DTG print station (see FilmSidesBadge). */}
            {film && (
              <FilmSidesBadge
                sides={{ isDual: film.isDual ?? false, isBackOnly: film.isBackOnly ?? false }}
                size="lg"
              />
            )}
            {pressed && (
              <Badge className="bg-green-100 text-green-700 hover:bg-green-100 dark:bg-green-900/50 dark:text-green-300 gap-1">
                <CheckCircle className="h-3.5 w-3.5" aria-hidden />{t('pressing.pressed')}
              </Badge>
            )}
          </div>
        </div>

        {/* Preview tiles + identity — Printer twin. ONE layout for customs and
            standards: the strip decides how many tiles a garment earns, fills
            this column with them, and takes its height from that width rather
            than from the image, so an image that arrives late or falls back
            cannot move the press buttons under the operator's hand.

            The PICTURE is the evidence and the text is the label on it, so on
            a wide card the preview column takes the room and the identity zone
            is pinned, exactly as on the print station's card. A fixed-width
            strip beside a flex-1 text column gave the thing being verified a
            quarter of the width and its caption three quarters. */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <div className="min-w-0 lg:flex-1">
            {/* No shape prop, and no sizing classes here: on a fill variant
                the shape is the tile COUNT, which the strip knows. A custom's
                one combined preview and a one-sided standard's single tile
                come out identical, both filling this column. */}
            <ProductionPreviewStrip
              entries={previewEntries}
              subject={displayName}
              variant="comparison"
              onOpen={onOpenPreview}
            />
          </div>
          <div className="flex min-w-0 flex-col gap-3 lg:w-72 lg:shrink-0">
            <div className="min-w-0 space-y-1">
              <p className="text-lg font-semibold leading-tight">{displayName}</p>
              <p className="font-mono text-sm text-muted-foreground break-all">{garment.sku}</p>
            </div>
            <div className="flex items-center gap-3">
              {garment.size && (
                <Badge variant="secondary" className="text-2xl font-bold px-4 py-2">
                  {garment.size}
                </Badge>
              )}
              {garment.color && (
                <span className="text-base font-medium">{garment.color}</span>
              )}
            </div>
            {filmReady && !pressed && (
              <p className="text-sm text-emerald-700 dark:text-emerald-300 font-medium flex items-center gap-1.5">
                <CheckCircle className="h-4 w-4" /> {t('pressing.filmOnGarment')}
              </p>
            )}
          </div>
        </div>

        {/* Film state banner inside the card (no film prepared). */}
        {!film && (
          <div className="mt-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 p-4 flex items-start gap-3">
            <AlertCircle className="h-6 w-6 text-amber-600 dark:text-amber-400 shrink-0" />
            <div>
              <p className="text-base font-semibold text-amber-800 dark:text-amber-200">{t('pressing.noFilm')}</p>
              <p className="text-sm text-amber-700 dark:text-amber-300">{t('pressing.noFilmDesc')}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
